import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  defineAsyncComponent,
  resolveDynamicComponent,
  ref,
} from 'vue'

import {
  CLIENT_ROLE_KEY,
  CLIENT_KEY,
  PARTNER_KEY,
  SELECTED_USER_ROLE_COOKIE_NAME,
} from '@/const'

import {
  useSubmit,
  catchFormErrors,
  defaultValidatorErrorsParser,
} from '@/composables/useSubmit'
import { useModalOpener } from '@/composables/useModalOpener'

import useGlobal from '@/composables/useGlobal'
import useField from '@/composables/useField'
import { composeTransformers, simplifiers } from '@/utils/transformers'

import {
  userRegister,
  userRequestConfirmation,
} from '@/services/registration.service'
import { setCookie } from '@/utils/cookies'


export default /*@__PURE__*/_defineComponent({
  __name: 'SignUp',
  props: {
    loginUrl: {},
    indexPageUrl: {},
    coordinatorData: {},
    coordinatorFillProfileUrl: {},
    initialCurrentUserType: {}
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const TokenConfirmationCheck = defineAsyncComponent(
  () => import('@app/token/ConfirmationCheck.vue')
)
const UiMessageModal = defineAsyncComponent(
  () => import('@/components/ui/modals/Message.vue')
)

const profileTransformFrom = composeTransformers(
  simplifiers.simple('password'),
  simplifiers.simple('tokenId'),
  simplifiers.simple('firstName'),
  simplifiers.simple('phone'),
  simplifiers.same('recaptcha'),
  simplifiers.same('coordinator')
)

const props = __props

const { $t } = useGlobal()
const userAgreement = window.user_agreement
const privacyPolicy = window.privacy_policy
const formRef = ref(null)

const form = ref({
  phone: '',
  coordinator: props.coordinatorData?.uuid || undefined,
  firstName: '',
  password: '',
  recaptcha: '',
  ...(props.coordinatorData || {}),
})
const captchaRef = ref(null)

const USER_ROLES = {
  [CLIENT_KEY]: {
    title: $t('Client'),
    resourceName: CLIENT_KEY,
    id: CLIENT_KEY,
    icon: 'i-user',
    hint: $t(
      'You can sign up as a client to book appointments with doctors, view medical records, and manage your health.'
    ),
    finish: () => {
      setCookie(SELECTED_USER_ROLE_COOKIE_NAME, CLIENT_ROLE_KEY)
    },
  },
  [PARTNER_KEY]: {
    title: $t('Partner'),
    resourceName: PARTNER_KEY,
    id: PARTNER_KEY,
    icon: 'i-shield',
    hint: $t(
      'You can sign up as a doctor or medical facility to manage patient appointments, access medical records, and provide healthcare services.'
    ),
  },
}
const USER_ROLE_MAP = [
  USER_ROLES[CLIENT_KEY],
  USER_ROLES[PARTNER_KEY],
]

const currentUserType = ref(
  USER_ROLES[props.initialCurrentUserType]
)

function onChangeRole(role) {
  currentUserType.value = role
}

const saveCaptcha = async (val, control, action) => {
  form.value.recaptcha = val
  control.setFieldError('nonFieldErrors', '')
  await catchFormErrors(
    send({
      ...form.value,
      token: form.value.phone,
      coordinator: props.coordinatorData?.uuid || undefined,
    }),
    control,
    defaultValidatorErrorsParser
  )
    .then(async (res: Response | { id: string }) => {
      if (res && (res as { id: string })?.id) {
        await action((res as { id: string }).id)
      } else {
        throw res
      }
      captchaRef.value.reset()
    })
    .catch((e) => {
      captchaRef.value.reset()
      console.warn(e)
    })
}

const isLoad = ref(false)

const getCaptcha = async () => {
  await captchaRef.value.execute()
}

const validateForm = async (f, action, control) => {
  const { valid } = await f()
  if (valid) {
    try {
      isLoad.value = false
      await action()
    } catch (e) {
      console.warn(e)
    }
  }
}
const FIELDS = ref([
  useField('phone', $t('Phone'), {
    component: 'd-control-phone',
    isRequired: true,
    readonly: !!props.coordinatorData,
    disabled: !!props.coordinatorData,
    'validate-on-blur': true,
    'validate-on-change': true,
    'validate-on-input': false,
    'validate-on-model-update': false,
  }),
  // useField('firstName', $t('First name'), ),
  useField('password', $t('Password'), { type: 'password', rules: 'required' }),
  useField('password2', $t('Repeat password'), {
    type: 'password',
    rules: 'confirmed:@password',
    isRequired: true,
  }),
])

const request = (sendData = form.value, control = formRef.value) => {
  isLoad.value = true
  return useSubmit(send(sendData), control)
}
const captchaConfirmedRef = ref(null)
const redirect = async (e) => {
  form.value.tokenId = e
  await captchaConfirmedRef.value.execute()
}
async function saveConfirmedCaptcha(e) {
  await catchFormErrors(
    userRegister
      .execute(
        { type: currentUserType.value.id },
        profileTransformFrom({
          ...form.value,
          recaptcha: e,
          coordinator: props.coordinatorData?.uuid || undefined,
        })
      )
      .then(() => {
        currentUserType.value?.finish?.()
        if (props.coordinatorData?.uuid) {
          window.location.assign(props.coordinatorFillProfileUrl)
        } else {
          window.location.reload()
        }
      })
      // const { open } = useModalOpener({
      //   name: 'UiMessageModal',
      //   component: UiMessageModal,
      //   message,
      //   redirect: {
      //     location: props.indexPageUrl,
      //   },
      //   classes: '',
      // })
      // open()
      .catch((e) => {
        throw e
      }),
    formRef.value,
    defaultValidatorErrorsParser
  )
}

const send = async (sendData) => {
  try {
    const data = await userRequestConfirmation.execute(
      { type: currentUserType.value.id },
      {
        ...sendData,
      }
    )
    if (captchaRef.value) {
      captchaRef.value.reset()
    }
    return data
  } catch (e) {
    isLoad.value = false
    captchaRef.value.reset()
    return Promise.reject(e)
  }
}

const __returned__ = { TokenConfirmationCheck, UiMessageModal, profileTransformFrom, props, $t, userAgreement, privacyPolicy, formRef, form, captchaRef, USER_ROLES, USER_ROLE_MAP, currentUserType, onChangeRole, saveCaptcha, isLoad, getCaptcha, validateForm, FIELDS, request, captchaConfirmedRef, redirect, saveConfirmedCaptcha, send, computed, defineAsyncComponent, resolveDynamicComponent, ref, get CLIENT_ROLE_KEY() { return CLIENT_ROLE_KEY }, get CLIENT_KEY() { return CLIENT_KEY }, get PARTNER_KEY() { return PARTNER_KEY }, get SELECTED_USER_ROLE_COOKIE_NAME() { return SELECTED_USER_ROLE_COOKIE_NAME }, get useSubmit() { return useSubmit }, get catchFormErrors() { return catchFormErrors }, get defaultValidatorErrorsParser() { return defaultValidatorErrorsParser }, get useModalOpener() { return useModalOpener }, get useGlobal() { return useGlobal }, get useField() { return useField }, get composeTransformers() { return composeTransformers }, get simplifiers() { return simplifiers }, get userRegister() { return userRegister }, get userRequestConfirmation() { return userRequestConfirmation }, get setCookie() { return setCookie } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})